import jsPDF from 'jspdf';
import { compressImageSrc } from './image-compression';
import { COMPANY, getCompanyContactLines } from './company-info';
import hoferLogoSrc from '@assets/hofer/hofer-logo.png';

// Hofer-Farbschema (identisch mit pdf-generator.ts)
const TEAL_R = 57, TEAL_G = 172, TEAL_B = 173;
const ORANGE_R = 255, ORANGE_G = 140, ORANGE_B = 0;
const SLATE_R = 68, SLATE_G = 85, SLATE_B = 110;
const MINT_R = 214, MINT_G = 236, MINT_B = 236;

function getImageFormat(dataUrl: string): string {
  if (dataUrl.startsWith('data:image/png')) return 'PNG';
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP';
  return 'JPEG';
}

export interface ProtokollPhoto {
  /** Dateiname des Fotos */
  fileName: string;
  /** Base64-Data-URL des Fotos */
  dataUrl: string;
  /** Optionale Beschreibung */
  description?: string | null;
}

export interface ProtokollSchritt {
  /** Freitext für diesen Arbeitsschritt */
  text: string;
  /** Fotos die zu diesem Schritt gehören */
  photos: ProtokollPhoto[];
}

export interface ProtokollData {
  /** Projektname */
  projectName: string;
  /** Kundenname */
  customerName: string;
  /** Projektadresse */
  projectAddress: string;
  /** Protokolldatum (ISO YYYY-MM-DD) */
  protokollDate: string;
  /** Arbeitsschritte (mindestens einer) */
  schritte: ProtokollSchritt[];
}

export class ProtokollPDFGenerator {
  private pdf!: jsPDF;
  private pageHeight!: number;
  private pageWidth!: number;
  private currentY!: number;
  private readonly margin = 15;
  private contentWidth!: number;
  private hoferLogoDataUrl: string | null = null;
  private hoferLogoAspect: number | null = null;

  private newDoc() {
    this.pdf = new jsPDF({ unit: 'mm', format: 'a4' });
    this.pageHeight = this.pdf.internal.pageSize.height;
    this.pageWidth = this.pdf.internal.pageSize.width;
    this.contentWidth = this.pageWidth - 2 * this.margin;
    this.currentY = 0;
  }

  private async loadLogo(): Promise<void> {
    try {
      const aspect = await new Promise<number>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const w = img.naturalWidth || img.width;
          const h = img.naturalHeight || img.height;
          if (!w || !h) { reject(new Error('Ungültige Logo-Dimensionen')); return; }
          resolve(w / h);
        };
        img.onerror = () => reject(new Error('Logo konnte nicht geladen werden'));
        img.src = hoferLogoSrc;
      });
      const dataUrl = await compressImageSrc(hoferLogoSrc, 600, 0.92);
      this.hoferLogoDataUrl = dataUrl;
      this.hoferLogoAspect = aspect;
    } catch {
      this.hoferLogoDataUrl = null;
      this.hoferLogoAspect = null;
    }
  }

  private checkPageBreak(needed: number) {
    if (this.currentY + needed > this.pageHeight - 34) {
      this.pdf.addPage();
      this.currentY = this.margin;
    }
  }

  /** Schlanker Hofer-Briefkopf (Logo + Markenname rechts + Teal-Linie) */
  private addHeaderBanner(): void {
    const topY = 12;
    const logoMaxH = 16;
    const logoMaxW = 60;
    let usedH = logoMaxH;

    if (this.hoferLogoDataUrl && this.hoferLogoAspect) {
      let logoW = logoMaxH * this.hoferLogoAspect;
      let logoH = logoMaxH;
      if (logoW > logoMaxW) { logoW = logoMaxW; logoH = logoMaxW / this.hoferLogoAspect; }
      try {
        this.pdf.addImage(
          this.hoferLogoDataUrl, getImageFormat(this.hoferLogoDataUrl),
          this.margin, topY, logoW, logoH, undefined, 'FAST',
        );
        usedH = logoH;
      } catch { /* ignore */ }
    }

    this.pdf.setTextColor(SLATE_R, SLATE_G, SLATE_B);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(9);
    this.pdf.text(`${COMPANY.name} · ${COMPANY.legalName}`, this.pageWidth - this.margin, topY + 4, { align: 'right' });
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(7.5);
    this.pdf.text(COMPANY.tagline, this.pageWidth - this.margin, topY + 8, { align: 'right' });

    const lineY = topY + Math.max(usedH, 10) + 3;
    this.pdf.setDrawColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.setLineWidth(0.4);
    this.pdf.line(this.margin, lineY, this.pageWidth - this.margin, lineY);

    this.currentY = lineY + 4;
  }

  /** Seite 1: Briefkopf + LEISTUNGSPROTOKOLL-Titel */
  private addHeader(data: ProtokollData): void {
    this.addHeaderBanner();

    // Titel
    this.pdf.setTextColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(26);
    const titleY = this.currentY + 10;
    this.pdf.text('LEISTUNGSPROTOKOLL', this.margin, titleY);
    this.pdf.setDrawColor(ORANGE_R, ORANGE_G, ORANGE_B);
    this.pdf.setLineWidth(1.4);
    this.pdf.line(this.margin, titleY + 2.4, this.margin + 70, titleY + 2.4);
    this.currentY = titleY + 7;

    // Sender-Zeile
    this.pdf.setTextColor(160, 160, 160);
    this.pdf.setFontSize(6.5);
    this.pdf.setFont('helvetica', 'normal');
    const senderLines = getCompanyContactLines();
    const senderLineGap = 2.6;
    senderLines.forEach((line, i) => {
      this.pdf.text(line, this.margin, this.currentY + i * senderLineGap);
    });
    const lastSenderY = this.currentY + (senderLines.length - 1) * senderLineGap;
    this.pdf.setDrawColor(210, 210, 210);
    this.pdf.setLineWidth(0.2);
    this.pdf.line(this.margin, lastSenderY + 1.2, this.margin + 95, lastSenderY + 1.2);
    this.currentY = lastSenderY + 10.6;

    // Kundenname + Adresse (links)
    const addressMaxW = 88;
    this.pdf.setTextColor(0, 0, 0);
    this.pdf.setFontSize(13);
    this.pdf.setFont('helvetica', 'bold');
    const nameLines = this.pdf.splitTextToSize(data.customerName || '—', addressMaxW);
    nameLines.slice(0, 2).forEach((line: string) => {
      this.pdf.text(line, this.margin, this.currentY);
      this.currentY += 5.6;
    });
    this.currentY += 0.4;
    if (data.projectAddress) {
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(10);
      data.projectAddress.split('\n').forEach(line => {
        if (!line.trim()) return;
        const wrapped = this.pdf.splitTextToSize(line, addressMaxW);
        wrapped.forEach((wl: string) => {
          this.pdf.text(wl, this.margin, this.currentY);
          this.currentY += 4.8;
        });
      });
    }

    // Info-Box rechts
    const protokollDateStr = data.protokollDate
      ? new Date(data.protokollDate + 'T12:00:00').toLocaleDateString('de-DE')
      : new Date().toLocaleDateString('de-DE');

    const boxX = this.margin + 95;
    const boxW = 85;
    const boxStartY = titleY + 7;
    const schrittCount = data.schritte.filter(s => s.text.trim()).length;
    const infoRows: [string, string][] = [
      ['Datum:', protokollDateStr],
      ['Projekt:', data.projectName || '—'],
      ['Arbeitsschritte:', String(schrittCount)],
    ];
    const rowH = 6.5;
    const boxH = 9 + infoRows.length * rowH;

    this.pdf.setFillColor(248, 251, 251);
    this.pdf.setDrawColor(MINT_R, MINT_G, MINT_B);
    this.pdf.setLineWidth(0.4);
    this.pdf.roundedRect(boxX, boxStartY, boxW, boxH, 2, 2, 'FD');

    this.pdf.setFillColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.roundedRect(boxX, boxStartY, boxW, 8, 2, 2, 'F');
    this.pdf.rect(boxX, boxStartY + 5, boxW, 3, 'F');
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(7.5);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text('PROTOKOLLINFORMATIONEN', boxX + boxW / 2, boxStartY + 5.5, { align: 'center' });

    let ry = boxStartY + 12;
    infoRows.forEach(([label, value]) => {
      this.pdf.setTextColor(100, 110, 110);
      this.pdf.setFontSize(8);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.text(label, boxX + 4, ry);
      this.pdf.setTextColor(20, 20, 20);
      this.pdf.setFont('helvetica', 'bold');
      const val = String(value).length > 24 ? String(value).slice(0, 22) + '…' : String(value);
      this.pdf.text(val, boxX + boxW - 4, ry, { align: 'right' });
      ry += rowH;
    });

    const boxBottom = boxStartY + boxH;
    if (this.currentY < boxBottom) this.currentY = boxBottom;
    this.currentY += 8;
  }

  /** Abschnittsheader für einen Arbeitsschritt */
  private addSchrittHeader(stepIndex: number, totalSchritte: number): void {
    this.checkPageBreak(14);
    const label = totalSchritte > 1
      ? `ARBEITSSCHRITT ${stepIndex + 1}`
      : 'ERBRACHTE LEISTUNGEN';

    this.pdf.setFillColor(TEAL_R, TEAL_G, TEAL_B);
    this.pdf.rect(this.margin, this.currentY, this.contentWidth, 7.5, 'F');
    this.pdf.setTextColor(255, 255, 255);
    this.pdf.setFontSize(9);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.text(label, this.margin + 4, this.currentY + 5.2);

    // Orange Akzentlinie am linken Rand
    this.pdf.setFillColor(ORANGE_R, ORANGE_G, ORANGE_B);
    this.pdf.rect(this.margin, this.currentY, 2, 7.5, 'F');

    this.currentY += 10;
  }

  /** Text eines Arbeitsschritts */
  private addSchrittText(text: string): void {
    if (!text.trim()) return;
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(30, 30, 30);
    const lineH = 5.0;
    const inputLines = text.split(/\r?\n/);
    inputLines.forEach((rawLine) => {
      const line = rawLine.trim().length > 0 ? rawLine : '';
      if (line === '') {
        this.currentY += lineH * 0.5;
        return;
      }
      const wrapped = this.pdf.splitTextToSize(line, this.contentWidth) as string[];
      wrapped.forEach((wl) => {
        this.checkPageBreak(lineH + 2);
        this.pdf.text(wl, this.margin, this.currentY);
        this.currentY += lineH;
      });
    });
    this.currentY += 4;
  }

  /** Fotos eines Arbeitsschritts (2 Spalten, inline) */
  private async addSchrittPhotos(photos: ProtokollPhoto[], stepIndex: number, totalSchritte: number): Promise<void> {
    if (photos.length === 0) return;

    const colGap = 5;
    const imgW = (this.contentWidth - colGap) / 2;
    const imgH = imgW * 0.75;
    const captionH = 8;
    const blockH = imgH + captionH + 4;

    // Fotos-Unterüberschrift
    this.checkPageBreak(10 + blockH);
    this.pdf.setFillColor(MINT_R, MINT_G, MINT_B);
    this.pdf.rect(this.margin, this.currentY, this.contentWidth, 6, 'F');
    this.pdf.setTextColor(SLATE_R, SLATE_G, SLATE_B);
    this.pdf.setFontSize(7.5);
    this.pdf.setFont('helvetica', 'bold');
    const fotoLabel = totalSchritte > 1
      ? `FOTOS ZU ARBEITSSCHRITT ${stepIndex + 1}`
      : 'FOTODOKUMENTATION';
    this.pdf.text(fotoLabel, this.margin + 4, this.currentY + 4.2);
    this.currentY += 9;

    // Fotos vorab komprimieren
    type ThumbInfo = { src: string } | null;
    const thumbs: ThumbInfo[] = await Promise.all(
      photos.map(async (p) => {
        try {
          const src = await compressImageSrc(p.dataUrl, 800, 0.8);
          return { src };
        } catch {
          return null;
        }
      })
    );

    let photoOffset = 0; // offset for photo numbering across the whole document
    for (let i = 0; i < photos.length; i += 2) {
      this.checkPageBreak(blockH + 4);
      const rowY = this.currentY;

      const renderPhoto = (photo: ProtokollPhoto, thumb: ThumbInfo, colX: number, localIdx: number) => {
        this.pdf.setDrawColor(MINT_R, MINT_G, MINT_B);
        this.pdf.setLineWidth(0.3);
        this.pdf.rect(colX, rowY, imgW, imgH, 'S');

        if (thumb) {
          try {
            this.pdf.addImage(thumb.src, getImageFormat(thumb.src), colX, rowY, imgW, imgH, undefined, 'FAST');
          } catch { /* ignore */ }
        }

        const captionY = rowY + imgH + 4;
        const caption = photo.description?.trim() || photo.fileName;
        const captionLines = this.pdf.splitTextToSize(caption, imgW) as string[];
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.setFontSize(7.5);
        this.pdf.setTextColor(60, 60, 60);
        captionLines.slice(0, 2).forEach((cl, ci) => {
          this.pdf.text(cl, colX, captionY + ci * 3.6);
        });

        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setFontSize(7);
        this.pdf.setTextColor(255, 255, 255);
        this.pdf.setFillColor(57, 172, 173);
        const numLabel = `${i + localIdx + 1 + photoOffset}`;
        const numW = numLabel.length * 2.5 + 3;
        this.pdf.rect(colX + 1, rowY + 1, numW, 4.5, 'F');
        this.pdf.text(numLabel, colX + 1 + numW / 2, rowY + 4.2, { align: 'center' });
      };

      renderPhoto(photos[i], thumbs[i], this.margin, 0);
      if (i + 1 < photos.length) {
        const col2X = this.margin + imgW + colGap;
        renderPhoto(photos[i + 1], thumbs[i + 1], col2X, 1);
      }

      this.currentY += blockH + 4;
    }
    this.currentY += 4;
  }

  /** Footer auf allen Seiten */
  private addFooterAllPages(): void {
    const totalPages = this.pdf.getNumberOfPages();
    const logoH = 9;
    const logoW = this.hoferLogoAspect ? logoH * this.hoferLogoAspect : 30;

    for (let p = 1; p <= totalPages; p++) {
      this.pdf.setPage(p);

      this.pdf.setDrawColor(TEAL_R, TEAL_G, TEAL_B);
      this.pdf.setLineWidth(0.5);
      this.pdf.line(this.margin, this.pageHeight - 28, this.pageWidth - this.margin, this.pageHeight - 28);

      let textStartX = this.margin;
      if (this.hoferLogoDataUrl) {
        try {
          this.pdf.addImage(
            this.hoferLogoDataUrl, getImageFormat(this.hoferLogoDataUrl),
            this.margin, this.pageHeight - 28 + 2, logoW, logoH,
          );
          textStartX = this.margin + logoW + 4;
        } catch { /* ignore */ }
      }
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(8);
      this.pdf.setTextColor(SLATE_R, SLATE_G, SLATE_B);
      this.pdf.text(`${COMPANY.name} · ${COMPANY.legalName}`, textStartX, this.pageHeight - 25);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(6.8);
      this.pdf.text(COMPANY.tagline, textStartX, this.pageHeight - 21.4);

      this.pdf.setDrawColor(200, 210, 210);
      this.pdf.setLineWidth(0.2);
      this.pdf.line(this.margin, this.pageHeight - 14, this.pageWidth - this.margin, this.pageHeight - 14);

      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(6.5);
      this.pdf.setTextColor(140, 140, 140);
      this.pdf.text(`Seite ${p} / ${totalPages}`, this.pageWidth - this.margin, this.pageHeight - 8, { align: 'right' });
    }
  }

  async generateProtokollPDF(data: ProtokollData): Promise<Blob> {
    this.newDoc();
    await this.loadLogo();

    this.addHeader(data);

    const filled = data.schritte.filter(s => s.text.trim() || s.photos.length > 0);
    const total = filled.length;

    for (let si = 0; si < filled.length; si++) {
      const schritt = filled[si];
      if (schritt.text.trim()) {
        this.addSchrittHeader(si, total);
        this.addSchrittText(schritt.text);
      }
      if (schritt.photos.length > 0) {
        await this.addSchrittPhotos(schritt.photos, si, total);
      }
      if (si < filled.length - 1) {
        this.currentY += 4;
      }
    }

    this.addFooterAllPages();
    return this.pdf.output('blob');
  }
}
